import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { FileDto, QuoteDto, QuoteItemKind, QuoteStatus, QuoteTemplateDto } from '../../../../shared/types';
import { purposeInfo } from './DealFiles';
import { saveBlob } from '../files';
import { WhatsAppButton } from '../whatsapp';
import type { Ctx } from '../ctx';
import { Icon } from '../icons';
import { euro, euroInput, parseEuro } from '../format';
import { Empty, ErrorBanner, Field, Modal, More, Panel, Pill } from '../ui';
import { DateInput } from '../pickers';
import { ask, askText } from '../dialogs';
import { SchedulePicker, newWhen, whenError, whenLabel, whenToIso, type When } from '../schedule';

const STATUS: Record<QuoteStatus, { label: string; tone: 'good' | 'warn' | 'bad' | 'info' | 'soft'; hint: string }> = {
  draft: { label: 'Bozza', tone: 'soft', hint: 'Ancora in preparazione' },
  ready: { label: 'Da inviare', tone: 'warn', hint: 'Pronti: da mandare al cliente' },
  sent: { label: 'Inviato', tone: 'info', hint: 'Mandati, in attesa di risposta' },
  accepted: { label: 'Accettato', tone: 'good', hint: 'Il cliente ha detto sì' },
  rejected: { label: 'Rifiutato', tone: 'bad', hint: 'Il cliente ha detto no' },
  expired: { label: 'Scaduto', tone: 'warn', hint: 'È passata la data di validità' },
};

const dateIt = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('it-IT') : '—');

function plusDays(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function num(v: string): number {
  const n = Number(v.trim().replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function calc(items: { quantity: number; unitCents: number }[], discountCents: number, vatPct: number) {
  const subtotal = items.reduce((n, i) => n + Math.round(i.quantity * i.unitCents), 0);
  const taxable = Math.max(0, subtotal - discountCents);
  const vat = Math.round((taxable * vatPct) / 100);
  return { subtotal, taxable, vat, total: taxable + vat };
}

/** Preventivi: chi li crea (consulente/commerciale) li gestisce; il cliente li vede e li accetta o rifiuta. */
export function QuotesPage({ ctx }: { ctx: Ctx }) {
  const { api, event, eventSeq } = ctx;
  const role = ctx.me.user.role;
  const isClient = role === 'client';
  const base = isClient ? '/api/client/quotes' : '/api/quotes';
  const [quotes, setQuotes] = useState<QuoteDto[] | null>(null);
  const [filter, setFilter] = useState<'all' | QuoteStatus>('all');
  const firstLoad = React.useRef(true);
  const [openId, setOpenId] = useState<number | null>((ctx.pageParam as { open?: number } | null)?.open ?? null);
  const [editing, setEditing] = useState<QuoteDto | 'new' | null>(null);
  const [prefill, setPrefill] = useState<Prefill | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Arrivo da una trattativa: apre subito l'editor gia compilato.
  useEffect(() => {
    const p = (ctx.pageParam as { newFromDeal?: Prefill } | null)?.newFromDeal;
    if (p) {
      setPrefill(p);
      setEditing('new');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    try {
      const list = await api.get<QuoteDto[]>(base);
      setQuotes(list);
      // All'apertura, se ci sono preventivi pronti, si parte da quelli
      if (firstLoad.current) {
        firstLoad.current = false;
        if (list.some((q) => q.status === 'ready')) setFilter('ready');
      }
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api, base]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (event?.type === 'quote.updated') void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventSeq]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: quotes?.length ?? 0 };
    for (const q of quotes ?? []) c[q.status] = (c[q.status] ?? 0) + 1;
    return c;
  }, [quotes]);

  const visible = (quotes ?? []).filter((q) => filter === 'all' || q.status === filter);
  const open = quotes?.find((q) => q.id === openId) ?? null;
  const tabs: ('all' | QuoteStatus)[] = isClient ? ['all', 'sent', 'accepted', 'rejected', 'expired'] : ['all', 'ready', 'draft', 'sent', 'accepted', 'rejected', 'expired'];

  const total = (list: QuoteDto[]) => list.reduce((n, q) => n + q.totalCents, 0);

  return (
    <div className="panels single">
      <Panel
        title="Preventivi"
        subtitle={isClient ? 'I preventivi che hai ricevuto' : quotes ? `${quotes.length} preventivi · accettati ${euro(total(quotes.filter((q) => q.status === 'accepted')))}` : undefined}
        right={
          !isClient && (
            <button type="button" className="btn dark" title="Prepara un preventivo da mandare a un cliente" onClick={() => setEditing('new')}>
              <Icon name="plus" size={16} /> Nuovo preventivo
            </button>
          )
        }
      >
        <ErrorBanner message={error} onClose={() => setError(null)} />
        <div className="quote-tabs">
          {tabs.map((t) => (
            <button
              key={t}
              type="button"
              className={`quote-tab${filter === t ? ' active' : ''}${t === 'ready' && (counts.ready ?? 0) > 0 ? ' ready' : ''}`}
              title={t === 'all' ? 'Tutti i preventivi' : STATUS[t].hint}
              onClick={() => setFilter(t)}
            >
              {t === 'all' ? 'Tutti' : STATUS[t].label}
              <b>{counts[t] ?? 0}</b>
            </button>
          ))}
        </div>
        {!quotes ? (
          <div className="muted small">Caricamento…</div>
        ) : visible.length === 0 ? (
          <Empty icon="note">{isClient ? 'Nessun preventivo ricevuto.' : 'Nessun preventivo. Creane uno con "Nuovo preventivo".'}</Empty>
        ) : (
          <div className="quote-list">
            {visible.map((q) => (
              <button key={q.id} type="button" className={`quote-row${q.status === 'ready' ? ' ready' : ''}`} onClick={() => setOpenId(q.id)}>
                <div className="quote-main">
                  <div className="quote-num">{q.number}</div>
                  <div className="quote-subj">{q.subject}</div>
                  <div className="muted small">
                    {q.recipientName}
                    {!isClient && role === 'admin' ? ` · ${q.createdByName}` : ''}
                    {' · '}
                    {dateIt(q.createdAt)}
                  </div>
                  {!isClient && q.status === 'ready' && (
                    <div className="quote-ready-line small">
                      Pronto{q.readyByName ? ` (da ${q.readyByName})` : ''}: al cliente {q.clientFile ? 'l’offerta impaginata' : 'il PDF del CRM'}
                      {q.advisorFile ? `, ${q.advisorName ? `a ${q.advisorName}` : 'al consulente'} la versione per il Ministero` : ''}
                    </div>
                  )}
                </div>
                <div className="quote-side">
                  <b>{euro(q.totalCents)}</b>
                  <Pill tone={STATUS[q.status].tone}>{STATUS[q.status].label}</Pill>
                </div>
              </button>
            ))}
          </div>
        )}
      </Panel>

      {open && (
        <QuoteView
          ctx={ctx}
          quote={open}
          base={base}
          onClose={() => setOpenId(null)}
          onChanged={(q) => {
            setQuotes((list) => (list ?? []).map((x) => (x.id === q.id ? q : x)));
          }}
          onEdit={() => {
            setEditing(open);
            setOpenId(null);
          }}
          onGone={() => {
            setOpenId(null);
            void load();
          }}
          onCreated={(q) => {
            void load().then(() => setOpenId(q.id));
          }}
        />
      )}

      {editing && (
        <QuoteEditor
          ctx={ctx}
          quote={editing === 'new' ? null : editing}
          prefill={editing === 'new' ? prefill : null}
          onClose={() => {
            setEditing(null);
            setPrefill(null);
          }}
          onSaved={(q) => {
            setEditing(null);
            void load().then(() => setOpenId(q.id));
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function QuoteView({
  ctx,
  quote: q,
  base,
  onClose,
  onChanged,
  onEdit,
  onGone,
  onCreated,
}: {
  ctx: Ctx;
  quote: QuoteDto;
  base: string;
  onClose: () => void;
  onChanged: (q: QuoteDto) => void;
  onEdit: () => void;
  onGone: () => void;
  onCreated: (q: QuoteDto) => void;
}) {
  const { api } = ctx;
  const role = ctx.me.user.role;
  const isClient = role === 'client';
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [deciding, setDeciding] = useState<'accepted' | 'rejected' | null>(null);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const pdf = () =>
    run(async () => {
      saveBlob(await api.download(`${base}/${q.id}/pdf`), !isClient && q.clientFile ? q.clientFile.name : `Preventivo ${q.number}.pdf`);
    });

  const setStatus = (status: 'draft' | 'ready' | 'sent' | 'accepted' | 'rejected') =>
    run(async () => {
      onChanged(await api.post<QuoteDto>(`/api/quotes/${q.id}/status`, { status }));
    });

  const duplicate = () =>
    run(async () => {
      onCreated(await api.post<QuoteDto>(`/api/quotes/${q.id}/duplicate`));
    });

  const remove = () =>
    run(async () => {
      if (!(await ask(`Eliminare la bozza ${q.number}?`))) return;
      await api.del(`/api/quotes/${q.id}`);
      onGone();
    });

  const isDraft = q.status === 'draft';
  const isReady = q.status === 'ready';
  const markReady = () =>
    run(async () => {
      const ok = await ask(
        `Segnare ${q.number} «pronto da inviare»? Arriva un avviso a Marco e Luca. Al cliente andrà: ${q.clientFile?.name ?? 'il PDF fatto dal CRM'}.`,
        { title: 'Pronto da inviare', ok: 'Sì, è pronto' },
      );
      if (ok) onChanged(await api.post<QuoteDto>(`/api/quotes/${q.id}/status`, { status: 'ready' }));
    });

  return (
    <Modal
      title={`Preventivo ${q.number}`}
      onClose={onClose}
      footer={
        <div className="quote-actions">
          <button
            type="button"
            className="btn"
            onClick={() => void pdf()}
            disabled={busy}
            title={isClient ? 'Scarica il preventivo in PDF' : 'Scarica il PDF da mandare al cliente, per inviarlo dalla tua posta'}
          >
            <Icon name="download" size={15} /> {isClient ? 'PDF' : 'PDF per il cliente'}
          </button>
          {isClient ? (
            q.status === 'sent' && (
              <>
                <button type="button" className="btn danger" onClick={() => setDeciding('rejected')} disabled={busy}>
                  Rifiuta
                </button>
                <button type="button" className="btn good" onClick={() => setDeciding('accepted')} disabled={busy}>
                  <Icon name="check" size={15} /> Accetta
                </button>
              </>
            )
          ) : (
            <>
              <button type="button" className="btn" onClick={() => void duplicate()} disabled={busy} title="Crea una copia da modificare">
                <Icon name="copy" size={15} /> Duplica
              </button>
              {isDraft && (
                <>
                  <button type="button" className="btn ghost" onClick={() => void remove()} disabled={busy} title="Elimina bozza">
                    <Icon name="trash" size={15} />
                  </button>
                  <button type="button" className="btn" onClick={onEdit} disabled={busy}>
                    <Icon name="edit" size={15} /> Modifica
                  </button>
                </>
              )}
              {isDraft && (
                <button type="button" className="btn good" onClick={() => void markReady()} disabled={busy} title="È finito: avvisa Marco e Luca che si può mandare al cliente">
                  <Icon name="check" size={15} /> Pronto da inviare
                </button>
              )}
              {isReady && (
                <button type="button" className="btn" onClick={() => void setStatus('draft')} disabled={busy} title="Serve ancora una modifica: torna in bozza">
                  Rimetti in bozza
                </button>
              )}
              {(isDraft || isReady || q.status === 'sent' || q.status === 'expired') && !isClient && (
                <button type="button" className="btn dark" onClick={() => setSending(true)} disabled={busy} title="Manda al cliente l’offerta per email, dalla tua casella">
                  <Icon name="send" size={15} /> Invia per email
                </button>
              )}
              {(isDraft || isReady) && (
                <button type="button" className="btn dark" onClick={() => void setStatus('sent')} disabled={busy} title="L’hai già mandato dalla tua posta sul PC: segnalo come inviato">
                  Segna come inviato
                </button>
              )}
              {(q.status === 'sent' || q.status === 'expired') && (
                <>
                  <button type="button" className="btn" onClick={() => void setStatus('rejected')} disabled={busy} title="Il cliente ha detto no">
                    Rifiutato
                  </button>
                  <button type="button" className="btn good" onClick={() => void setStatus('accepted')} disabled={busy} title="Il cliente ha detto sì: dopo puoi fare la fattura">
                    Accettato
                  </button>
                </>
              )}
              {q.status === 'accepted' && (
                <button
                  type="button"
                  className="btn dark"
                  disabled={busy}
                  title="Prepara la fattura con le righe di questo preventivo"
                  onClick={() =>
                    void run(async () => {
                      const inv = await api.post<{ id: number }>(`/api/invoices/from-quote/${q.id}`);
                      ctx.setPage('invoices', { open: inv.id });
                    })
                  }
                >
                  <Icon name="note" size={15} /> Crea fattura
                </button>
              )}
              {(q.status === 'accepted' || q.status === 'rejected') && (
                <button type="button" className="btn" onClick={() => void setStatus('sent')} disabled={busy} title="Torna «inviato», in attesa di risposta">
                  Riapri
                </button>
              )}
            </>
          )}
        </div>
      }
    >
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <div className="quote-doc">
        <div className="quote-doc-head">
          <div>
            <div className="quote-subj big">{q.subject}</div>
            <div className="muted small">
              {q.number} · {dateIt(q.createdAt)}
              {q.validUntil ? ` · valido fino al ${dateIt(q.validUntil)}` : ''}
            </div>
          </div>
          <Pill tone={STATUS[q.status].tone}>{STATUS[q.status].label}</Pill>
        </div>
        <div className="quote-to">
          <b>{q.recipientName}</b>
          {q.recipientAddress && <div className="muted small">{q.recipientAddress}</div>}
          {q.recipientVat && <div className="muted small">P.IVA / C.F.: {q.recipientVat}</div>}
          {q.recipientEmail && <div className="muted small">{q.recipientEmail}</div>}
        </div>
        {!isClient && q.dealId && <QuoteDocs ctx={ctx} quote={q} editable={isDraft || isReady} onChanged={onChanged} />}
        {q.intro && <p className="quote-text">{q.intro}</p>}
        <table className="table quote-items">
          <thead>
            <tr>
              <th>Descrizione</th>
              <th style={{ textAlign: 'right' }}>Q.tà</th>
              <th style={{ textAlign: 'right' }}>Prezzo</th>
              <th style={{ textAlign: 'right' }}>Importo</th>
            </tr>
          </thead>
          <tbody>
            {q.items.map((i, k) => (
              <tr key={k}>
                <td>{i.description}</td>
                <td style={{ textAlign: 'right' }}>
                  {i.quantity.toLocaleString('it-IT')}
                  {i.kind === 'hours' ? ' h' : ''}
                </td>
                <td style={{ textAlign: 'right' }}>{euro(i.unitCents)}</td>
                <td style={{ textAlign: 'right' }}>{euro(i.totalCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="quote-totals">
          <div>
            <span>Imponibile</span>
            <span>{euro(q.subtotalCents)}</span>
          </div>
          {q.discountCents > 0 && (
            <div>
              <span>Sconto</span>
              <span>− {euro(q.discountCents)}</span>
            </div>
          )}
          <div>
            <span>{q.vatPct > 0 ? `IVA ${q.vatPct.toLocaleString('it-IT')}%` : 'IVA'}</span>
            <span>{q.vatPct > 0 ? euro(q.vatCents) : 'non applicata'}</span>
          </div>
          <div className="grand">
            <span>Totale</span>
            <span>{euro(q.totalCents)}</span>
          </div>
        </div>
        {q.notes && (
          <div className="quote-notes">
            <div className="muted small">Note e condizioni</div>
            <p className="quote-text">{q.notes}</p>
          </div>
        )}
        {(q.status === 'accepted' || q.status === 'rejected') && (
          <div className={`mail-note`}>
            {q.status === 'accepted' ? 'Accettato' : 'Rifiutato'} il {dateIt(q.decidedAt)}
            {q.decisionNote ? ` — “${q.decisionNote}”` : ''}
          </div>
        )}
      </div>

      {sending && (
        <SendModal
          ctx={ctx}
          quote={q}
          onClose={() => setSending(false)}
          onSent={(u) => {
            setSending(false);
            onChanged(u);
          }}
        />
      )}
      {deciding && (
        <DecisionModal
          decision={deciding}
          onClose={() => setDeciding(null)}
          onConfirm={async (note) => {
            const u = await api.post<QuoteDto>(`/api/client/quotes/${q.id}/decision`, { decision: deciding, note: note || undefined });
            setDeciding(null);
            onChanged(u);
          }}
        />
      )}
    </Modal>
  );
}

function DecisionModal({ decision, onClose, onConfirm }: { decision: 'accepted' | 'rejected'; onClose: () => void; onConfirm: (note: string) => Promise<void> }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const accept = decision === 'accepted';
  return (
    <Modal
      narrow
      title={accept ? 'Accetta il preventivo' : 'Rifiuta il preventivo'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Annulla
          </button>
          <button
            type="button"
            className={`btn ${accept ? 'good' : 'danger'}`}
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm(note.trim());
              } catch (e) {
                setError((e as Error).message);
                setBusy(false);
              }
            }}
          >
            {accept ? 'Conferma accettazione' : 'Conferma rifiuto'}
          </button>
        </>
      }
    >
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <Field label="Nota (facoltativa)">
        <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder={accept ? 'Es. procediamo pure' : 'Motivo del rifiuto'} />
      </Field>
    </Modal>
  );
}

function SendModal({ ctx, quote: q, onClose, onSent }: { ctx: Ctx; quote: QuoteDto; onClose: () => void; onSent: (q: QuoteDto) => void }) {
  const [to, setTo] = useState(q.recipientEmail ?? '');
  const [subject, setSubject] = useState(`Preventivo ${q.number} - ${q.subject}`);
  const [text, setText] = useState(
    `Buongiorno ${q.recipientName},\n\nin allegato il preventivo ${q.number} "${q.subject}"${q.validUntil ? `, valido fino al ${dateIt(q.validUntil)}` : ''}.\n\nRestiamo a disposizione per qualsiasi chiarimento.\n\nCordiali saluti,\n${ctx.me.user.name}`,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [when, setWhen] = useState<When | null>(null);
  const [scheduledFor, setScheduledFor] = useState<string | null>(null);
  const [extras, setExtras] = useState<FileDto[]>([]);
  const [picked, setPicked] = useState<number[]>([]);

  // Altri documenti della trattativa segnati «per il cliente» (es. lo schema degli interventi): si possono aggiungere
  useEffect(() => {
    if (!q.dealId) return;
    ctx.api
      .get<FileDto[]>(`/api/deals/${q.dealId}/files`)
      .then((l) => setExtras(l.filter((f) => f.purpose === 'client' && f.id !== q.clientFile?.id)))
      .catch(() => undefined);
  }, [ctx.api, q.dealId, q.clientFile?.id]);

  const send = async () => {
    const list = to.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
    if (!list.length) return setError('Inserisci il destinatario');
    if (when) {
      const bad = whenError(when);
      if (bad) return setError(bad);
    }
    setBusy(true);
    setError(null);
    try {
      const sendAt = whenToIso(when) ?? undefined;
      const r = await ctx.api.post<QuoteDto>(`/api/quotes/${q.id}/send`, { to: list, subject, text, sendAt, extraFileIds: picked });
      if (sendAt) {
        setScheduledFor(sendAt);
        setBusy(false);
        return;
      }
      onSent(r);
    } catch (e) {
      const msg = (e as Error).message;
      setError(/non configurata/i.test(msg) ? 'Collega prima la tua casella nella sezione Posta.' : msg);
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Invia per email"
      onClose={onClose}
      footer={
        scheduledFor ? (
          <button type="button" className="btn dark" onClick={onClose}>
            Chiudi
          </button>
        ) : (
          <>
            <button type="button" className="btn" onClick={onClose} disabled={busy}>
              Annulla
            </button>
            {!when && (
              <button type="button" className="btn" onClick={() => setWhen(newWhen())} disabled={busy} title="Scegli giorno e ora dell'invio">
                <Icon name="clock" size={15} /> Programma
              </button>
            )}
            <button type="button" className="btn dark" onClick={() => void send()} disabled={busy}>
              <Icon name={when ? 'clock' : 'send'} size={15} /> {busy ? (when ? 'Salvataggio…' : 'Invio…') : when ? 'Programma invio' : 'Invia con PDF allegato'}
            </button>
          </>
        )
      }
    >
      {scheduledFor ? (
        <div className="mail-note" title="Il preventivo diventa «inviato» quando la mail parte. Orario e annullamento: Posta → Programmate.">
          Partirà <b>{whenLabel(scheduledFor)}</b>: la trovi in Posta → Programmate.
        </div>
      ) : (
      <>
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <Field label="A">
        <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="cliente@esempio.it" />
      </Field>
      <Field label="Oggetto">
        <input value={subject} onChange={(e) => setSubject(e.target.value)} />
      </Field>
      <Field label="Messaggio">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={9} style={{ minHeight: 180 }} />
      </Field>
      <div className="send-attach">
        <div className="muted small">Allegati per il cliente</div>
        <label className="send-attach-row">
          <input type="checkbox" checked disabled /> <b>{q.clientFile?.name ?? `Preventivo ${q.number}.pdf (fatto dal CRM)`}</b>
        </label>
        {extras.map((f) => (
          <label key={f.id} className="send-attach-row">
            <input
              type="checkbox"
              checked={picked.includes(f.id)}
              onChange={(e) => setPicked((p) => (e.target.checked ? [...p, f.id] : p.filter((x) => x !== f.id)))}
            />{' '}
            {f.name}
          </label>
        ))}
        {q.advisorFile && (
          <div className="muted small">
            La versione per il Ministero non parte mai al cliente: va solo {q.advisorName ? `a ${q.advisorName}` : 'al consulente'}.
          </div>
        )}
      </div>
      {when && <SchedulePicker value={when} onChange={setWhen} onClear={() => setWhen(null)} />}
      </>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------

interface ItemDraft {
  description: string;
  kind: QuoteItemKind;
  quantity: string;
  unit: string;
}

interface Prefill {
  dealId: number | null;
  name: string;
  email: string;
  value: number;
}

function QuoteEditor({ ctx, quote, prefill, onClose, onSaved }: { ctx: Ctx; quote: QuoteDto | null; prefill: Prefill | null; onClose: () => void; onSaved: (q: QuoteDto) => void }) {
  const { api } = ctx;
  const [clients, setClients] = useState<{ id: number; name: string }[]>([]);
  const [clientId, setClientId] = useState<number | null>(quote?.clientId ?? null);
  const [name, setName] = useState(quote?.recipientName ?? prefill?.name ?? '');
  const [email, setEmail] = useState(quote?.recipientEmail ?? prefill?.email ?? '');
  const [vat, setVat] = useState(quote?.recipientVat ?? '');
  const [address, setAddress] = useState(quote?.recipientAddress ?? '');
  const [subject, setSubject] = useState(quote?.subject ?? '');
  const [intro, setIntro] = useState(quote?.intro ?? '');
  const [notes, setNotes] = useState(quote?.notes ?? '');
  const [vatPct, setVatPct] = useState(quote ? String(quote.vatPct).replace('.', ',') : '22');
  const [discount, setDiscount] = useState(quote && quote.discountCents ? euroInput(quote.discountCents) : '');
  const [validUntil, setValidUntil] = useState(quote ? (quote.validUntil ?? '') : plusDays(30));
  const [items, setItems] = useState<ItemDraft[]>(
    quote
      ? quote.items.map((i) => ({ description: i.description, kind: i.kind, quantity: String(i.quantity).replace('.', ','), unit: euroInput(i.unitCents) }))
      : [{ description: '', kind: 'fixed', quantity: '1', unit: prefill?.value ? euroInput(prefill.value) : '' }],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<QuoteTemplateDto[]>([]);
  const [tplId, setTplId] = useState<number | ''>('');

  useEffect(() => {
    api.get<{ id: number; name: string }[]>('/api/quotes/meta/clients').then(setClients).catch(() => undefined);
    api.get<QuoteTemplateDto[]>('/api/templates').then(setTemplates).catch(() => undefined);
  }, [api]);

  const applyTemplate = (id: number | '') => {
    setTplId(id);
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setSubject(t.subject);
    setIntro(t.intro ?? '');
    setNotes(t.notes ?? '');
    setVatPct(String(t.vatPct).replace('.', ','));
    setItems(t.items.map((i) => ({ description: i.description, kind: i.kind, quantity: String(i.quantity).replace('.', ','), unit: euroInput(i.unitCents) })));
  };

  const saveAsTemplate = async () => {
    if (items.some((i) => !i.description.trim())) return setError('Ogni riga deve avere una descrizione');
    const name = await askText('Nome del modello', subject.trim() || 'Nuovo modello');
    if (!name?.trim()) return;
    try {
      const t = await api.post<QuoteTemplateDto>('/api/templates', {
        name: name.trim(),
        subject: subject.trim(),
        intro: intro.trim() || null,
        notes: notes.trim() || null,
        vatPct: num(vatPct),
        items: items.map((i, k) => ({ description: i.description.trim(), kind: i.kind, quantity: parsed[k].quantity, unitCents: parsed[k].unitCents })),
      });
      setTemplates((l) => [...l, t].sort((a, b) => a.name.localeCompare(b.name)));
      setTplId(t.id);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const deleteTemplate = async () => {
    const t = templates.find((x) => x.id === tplId);
    if (!t || !(await ask(`Eliminare il modello "${t.name}"?`))) return;
    try {
      await api.del(`/api/templates/${t.id}`);
      setTemplates((l) => l.filter((x) => x.id !== t.id));
      setTplId('');
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const parsed = items.map((i) => ({ quantity: num(i.quantity), unitCents: parseEuro(i.unit) ?? 0 }));
  const t = calc(parsed, parseEuro(discount) ?? 0, num(vatPct));

  const setItem = (idx: number, patch: Partial<ItemDraft>) => setItems((list) => list.map((x, k) => (k === idx ? { ...x, ...patch } : x)));

  const pickClient = (id: number | null) => {
    setClientId(id);
    const c = clients.find((x) => x.id === id);
    if (c && !name.trim()) setName(c.name);
  };

  const save = async () => {
    if (!name.trim()) return setError('Indica il destinatario');
    if (!subject.trim()) return setError('Indica l’oggetto');
    if (items.some((i) => !i.description.trim())) return setError('Ogni riga deve avere una descrizione');
    setBusy(true);
    setError(null);
    const body = {
      clientId,
      dealId: quote?.dealId ?? prefill?.dealId ?? null,
      recipientName: name.trim(),
      recipientEmail: email.trim() || null,
      recipientVat: vat.trim() || null,
      recipientAddress: address.trim() || null,
      subject: subject.trim(),
      intro: intro.trim() || null,
      notes: notes.trim() || null,
      vatPct: num(vatPct),
      discountCents: parseEuro(discount) ?? 0,
      validUntil: validUntil || null,
      items: items.map((i, k) => ({ description: i.description.trim(), kind: i.kind, quantity: parsed[k].quantity, unitCents: parsed[k].unitCents })),
    };
    try {
      onSaved(quote ? await api.put<QuoteDto>(`/api/quotes/${quote.id}`, body) : await api.post<QuoteDto>('/api/quotes', body));
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={quote ? `Modifica ${quote.number}` : 'Nuovo preventivo'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={() => void saveAsTemplate()} disabled={busy} title="Salva righe e testi come modello riutilizzabile">
            <Icon name="copy" size={15} /> Salva come modello
          </button>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Annulla
          </button>
          <button type="button" className="btn dark" onClick={() => void save()} disabled={busy}>
            {busy ? 'Salvataggio…' : 'Salva bozza'}
          </button>
        </>
      }
    >
      <ErrorBanner message={error} onClose={() => setError(null)} />
      {templates.length > 0 && (
        <Field label="Parti da un modello">
          <div className="row-actions" style={{ margin: 0 }}>
            <select value={tplId} onChange={(e) => applyTemplate(e.target.value ? Number(e.target.value) : '')} style={{ flex: 1 }}>
              <option value="">— Nessuno —</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            {tplId !== '' && (
              <button type="button" className="btn ghost" onClick={() => void deleteTemplate()} title="Elimina modello">
                <Icon name="trash" size={14} />
              </button>
            )}
          </div>
        </Field>
      )}
      {clients.length > 0 && (
        <Field label="Cliente esistente" hint="Se lo colleghi, il cliente vedrà il preventivo nella sua area e potrà accettarlo.">
          <select value={clientId ?? ''} onChange={(e) => pickClient(e.target.value ? Number(e.target.value) : null)}>
            <option value="">— Potenziale cliente / non collegato —</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
      )}
      <div className="fields-2">
        <Field label="Destinatario">
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Email">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
      </div>
      <Field label="Oggetto">
        <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Es. Sviluppo sito web" />
      </Field>
      <More label="Dati del destinatario e introduzione" open={!!(vat || address || intro)}>
        <div className="fields-2">
          <Field label="P.IVA / Codice fiscale">
            <input value={vat} onChange={(e) => setVat(e.target.value)} />
          </Field>
          <Field label="Indirizzo">
            <input value={address} onChange={(e) => setAddress(e.target.value)} />
          </Field>
        </div>
        <Field label="Introduzione (facoltativa)">
          <textarea value={intro} onChange={(e) => setIntro(e.target.value)} rows={3} />
        </Field>
      </More>

      <div className="quote-edit-items">
        <div className="muted small" style={{ marginBottom: 6 }}>
          Righe
        </div>
        {items.map((it, idx) => (
          <div key={idx} className="quote-edit-row">
            <input className="qe-desc" value={it.description} onChange={(e) => setItem(idx, { description: e.target.value })} placeholder="Descrizione" />
            <select className="qe-kind" value={it.kind} onChange={(e) => setItem(idx, { kind: e.target.value as QuoteItemKind })}>
              <option value="fixed">A corpo</option>
              <option value="hours">Ore</option>
            </select>
            <input className="qe-num" value={it.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })} inputMode="decimal" title="Quantità" />
            <input className="qe-num" value={it.unit} onChange={(e) => setItem(idx, { unit: e.target.value })} inputMode="decimal" placeholder="€" title="Prezzo unitario" />
            <span className="qe-tot">{euro(Math.round(parsed[idx].quantity * parsed[idx].unitCents))}</span>
            <button type="button" className="btn icon small ghost" disabled={items.length === 1} onClick={() => setItems((l) => l.filter((_, k) => k !== idx))} title="Rimuovi riga">
              <Icon name="x" size={14} />
            </button>
          </div>
        ))}
        <button type="button" className="btn small" onClick={() => setItems((l) => [...l, { description: '', kind: 'fixed', quantity: '1', unit: '' }])}>
          <Icon name="plus" size={14} /> Aggiungi riga
        </button>
      </div>

      <div style={{ height: 14 }} />
      <More label="Sconto, IVA, validità e note" open={!!(discount.trim() || notes.trim())}>
        <div className="fields-3">
          <Field label="Sconto (€)">
            <input value={discount} onChange={(e) => setDiscount(e.target.value)} inputMode="decimal" placeholder="0" />
          </Field>
          <Field label="IVA (%)" hint="0 se non applicabile (es. regime forfettario)">
            <input value={vatPct} onChange={(e) => setVatPct(e.target.value)} inputMode="decimal" />
          </Field>
          <Field label="Valido fino al">
            <DateInput value={validUntil} onChange={setValidUntil} allowEmpty />
          </Field>
        </div>
        <Field label="Note e condizioni (facoltative)">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Modalità di pagamento, tempi di consegna…" />
        </Field>
      </More>

      <div className="quote-totals">
        <div>
          <span>Imponibile</span>
          <span>{euro(t.subtotal)}</span>
        </div>
        <div>
          <span>IVA</span>
          <span>{euro(t.vat)}</span>
        </div>
        <div className="grand">
          <span>Totale</span>
          <span>{euro(t.total)}</span>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Cosa mandare e a chi: il PDF per il cliente e quello per il consulente della pratica (es. Ferrone),
 * presi dai documenti della trattativa. Così non si confondono.
 */
function QuoteDocs({ ctx, quote: q, editable, onChanged }: { ctx: Ctx; quote: QuoteDto; editable: boolean; onChanged: (q: QuoteDto) => void }) {
  const { api } = ctx;
  const [files, setFiles] = useState<FileDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [changing, setChanging] = useState<'client' | 'advisor' | null>(null);

  useEffect(() => {
    if (!q.dealId) return;
    api.get<FileDto[]>(`/api/deals/${q.dealId}/files`).then(setFiles).catch((e) => setError((e as Error).message));
  }, [api, q.dealId]);

  const save = async (clientFileId: number | null, advisorFileId: number | null) => {
    try {
      onChanged(await api.put<QuoteDto>(`/api/quotes/${q.id}/files`, { clientFileId, advisorFileId }));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const download = async (f: { id: number; name: string }) => {
    try {
      saveBlob(await api.download(`/api/files/${f.id}/download`), f.name);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  // «a Ferrone» ma «al consulente»: mai «a il consulente»
  const toWho = q.advisorName ? `a ${q.advisorName}` : 'al consulente';
  const rows: { key: 'client' | 'advisor'; title: string; hint: string; file: QuoteDto['clientFile']; empty: string }[] = [
    {
      key: 'client',
      title: 'Al cliente',
      hint: 'Questo è il PDF che parte con «Invia per email» e che scarichi con «PDF per il cliente».',
      file: q.clientFile,
      empty: 'Nessuna offerta collegata: partirebbe il PDF semplice fatto dal CRM.',
    },
    {
      key: 'advisor',
      title: toWho.charAt(0).toUpperCase() + toWho.slice(1),
      hint: `Versione per il Ministero, serve ${toWho} per la pratica. Non mandarla al cliente.`,
      file: q.advisorFile,
      empty: 'Nessun documento collegato.',
    },
  ];

  return (
    <div className="quote-docs">
      <div className="muted small">Cosa mandare e a chi</div>
      {error && <div className="small" style={{ color: 'var(--bad)' }}>{error}</div>}
      {rows.map((r) => {
        const info = purposeInfo(r.key, q.advisorName);
        // Al cliente si sceglie l'offerta; lo schema e gli altri documenti si aggiungono come allegati in «Invia per email»
        const options = (files ?? []).filter((f) => f.purpose === r.key && (r.key !== 'client' || !/^Schema degli interventi/i.test(f.name)));
        return (
          <div key={r.key} className={`quote-doc-row ${info.tone}`}>
            <div className="quote-doc-who">
              <b>{r.title}</b>
              <span className="muted small">{r.hint}</span>
            </div>
            <div className="quote-doc-file">
              {editable && files && changing === r.key ? (
                <select
                  autoFocus
                  value={r.file?.id ?? ''}
                  onBlur={() => setChanging(null)}
                  onChange={(e) => {
                    const v = e.target.value ? Number(e.target.value) : null;
                    setChanging(null);
                    void save(r.key === 'client' ? v : (q.clientFile?.id ?? null), r.key === 'advisor' ? v : (q.advisorFile?.id ?? null));
                  }}
                >
                  {options.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                  <option value="">{r.key === 'client' ? 'Nessuna offerta: il PDF semplice fatto dal CRM' : 'Nessun documento'}</option>
                </select>
              ) : (
                <span className={r.file ? 'quote-doc-name' : 'muted small'}>{r.file?.name ?? r.empty}</span>
              )}
              {editable && files && changing !== r.key && options.length > (r.file ? 1 : 0) && (
                <button type="button" className="btn ghost small" onClick={() => setChanging(r.key)} title="Scegli un altro documento tra quelli della trattativa">
                  Cambia
                </button>
              )}
              {r.file && (
                <button type="button" className="btn ghost small" onClick={() => void download(r.file!)} title="Scarica">
                  <Icon name="download" size={14} />
                </button>
              )}
              {r.file && (
                <WhatsAppButton
                  ctx={ctx}
                  file={{ id: r.file.id, name: r.file.name, mime: 'application/pdf', purpose: r.key }}
                  prefer={r.key === 'advisor' ? q.advisorName : q.recipientName}
                  advisor={q.advisorName}
                  className="btn ghost small wa-btn"
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
